import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import Card from '../components/Card';
import ErrorMessage from '../components/ErrorMessage';
import { logger } from '../utils/logger';

interface Activity {
  _id: string;
  user: {
    firstName: string;
    lastName: string;
  };
  action: string;
  entityType: string;
  details: string;
  familyGroup?: {
    name: string;
  } | null;
  createdAt: string;
}

const Activity: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchActivity = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/activity');
      setActivities(response.data);
      setError(null);
    } catch (error) {
      logger.error('Error fetching activity:', error);
      setError('Failed to load activity history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, []);

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'create':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'update':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'delete':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getActivityColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-500';
      case 'update':
        return 'bg-blue-500';
      case 'delete':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatActivityMessage = (activity: Activity): string => {
    const actor = `${activity.user.firstName} ${activity.user.lastName}`;
    const action = activity.action.toLowerCase();
    const entityType = activity.entityType === 'family_group' ? 'family group' : activity.entityType;
    const groupContext = activity.familyGroup ? `in ${activity.familyGroup.name}` : '';
    
    return `${actor} ${action}d ${entityType} ${activity.details} ${groupContext}`.trim();
  };

  return (
    <div className="py-6">
      <div className="w-full px-6">
        <Card>
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Activity</h1>
          </div>

          <div className="p-6">
            {error && (
              <ErrorMessage
                message={error}
                className="mb-4"
                onDismiss={() => setError(null)}
              />
            )}

            {isLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-gray-100 p-4 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/4 mt-2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flow-root">
                <ul role="list" className="-mb-8">
                  {activities.length > 0 ? (
                    activities.map((activity, index) => (
                      <li key={activity._id}>
                        <div className="relative pb-8">
                          {index < activities.length - 1 ? (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full ${getActivityColor(activity.action)} flex items-center justify-center ring-8 ring-white`}>
                                {getActivityIcon(activity.action)}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {formatActivityMessage(activity)}
                                </p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                <time dateTime={activity.createdAt}>
                                  {format(new Date(activity.createdAt), 'MMM d, yyyy h:mm a')}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      No activity to display
                    </div>
                  )}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Activity; 